import { ArrowLeft } from 'lucide-react'
import type { ReactNode } from 'react'
import { Link } from 'react-router'
import { PageHeader } from '@/design-system/components/PageHeader/PageHeader'

export interface DetailHeaderTab<TKey extends string = string> {
  key: TKey
  label: string
  badge?: ReactNode
}

export interface DetailHeaderProps<TKey extends string = string> {
  /** Lien de retour vers la liste d'où vient la fiche. */
  back?: { to: string; label: string }
  /** Identifiant du dossier — numéro de Client, de Contrat, de Facture. */
  eyebrow?: string
  title: string
  subtitle?: string
  badge?: ReactNode
  actions?: ReactNode
  tabs?: DetailHeaderTab<TKey>[]
  activeTab?: TKey
  onSelectTab?: (key: TKey) => void
  tabsLabel?: string
}

/**
 * En-tête commun aux fiches de détail — Client, Contrat, Facture, Lead.
 *
 * Six modules composaient leur en-tête différemment : retour tantôt présent
 * tantôt absent, identifiant tantôt en surtitre tantôt fondu dans le titre,
 * onglets propres à chaque fiche. L'ordre est ici le même partout : retour,
 * puis identité et statut, puis actions, puis onglets.
 */
export function DetailHeader<TKey extends string = string>({
  back,
  eyebrow,
  title,
  subtitle,
  badge,
  actions,
  tabs,
  activeTab,
  onSelectTab,
  tabsLabel = 'Sections de la fiche',
}: DetailHeaderProps<TKey>) {
  return (
    <div className="ds-detail-header">
      {back ? (
        <Link className="ds-detail-header__back" to={back.to}>
          <ArrowLeft aria-hidden="true" size={14} /> {back.label}
        </Link>
      ) : null}
      <PageHeader
        {...(eyebrow ? { eyebrow } : {})}
        title={title}
        {...(subtitle ? { subtitle } : {})}
        {...(badge ? { badge } : {})}
        {...(actions ? { actions } : {})}
      />
      {tabs && tabs.length > 0 ? (
        <div className="ds-detail-tabs" role="tablist" aria-label={tabsLabel}>
          {tabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.key}
              className="ds-detail-tabs__item"
              onClick={() => {
                onSelectTab?.(tab.key)
              }}
            >
              {tab.label}
              {tab.badge}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  )
}
